<div fxLayout="column" fxLayoutGap="12px" data-testid="module-config-table-columns">
    <div fxLayout="row" fxLayoutAlign="space-between center" fxLayoutGap="8px">
        <h4 class="m-a-0">{{ "modules.fieldConfig.table.title" | translate }}</h4>

        <div fxLayout="row" fxLayoutAlign="end center" fxLayoutGap="8px">
            @if (editing()) {
                <button
                    mat-button
                    data-testid="module-config-table-columns-action-cancel"
                    [disabled]="saving()"
                    (click)="cancelEditing()">
                    {{ "actions.cancel" | translate }}
                </button>
                <button
                    mat-flat-button
                    color="primary"
                    data-testid="module-config-table-columns-action-save"
                    [disabled]="saving()"
                    (click)="save()">
                    <mat-icon aria-hidden="true">check</mat-icon>
                    {{ "actions.save" | translate }}
                </button>
            } @else if (isTable() && !hasNoColumns() && !loading()) {
                <button
                    mat-stroked-button
                    data-testid="module-config-table-columns-action-add"
                    [matMenuTriggerFor]="addColumnMenu"
                    [disabled]="isBusy()">
                    <mat-icon aria-hidden="true">add</mat-icon>
                    {{ "modules.screenDesigner.addField" | translate }}
                    <mat-icon iconPositionEnd aria-hidden="true">arrow_drop_down</mat-icon>
                </button>
                <button
                    mat-stroked-button
                    data-testid="module-config-table-columns-action-edit"
                    [disabled]="isBusy()"
                    (click)="startEditing()">
                    <mat-icon aria-hidden="true">view_column</mat-icon>
                    {{ "modules.fieldConfig.table.editTable" | translate }}
                </button>
            }
        </div>
    </div>

    @if (loading() && !columns().length) {
        <ngx-skeleton-loader
            count="1"
            role="status"
            [attr.aria-busy]="true"
            [attr.aria-label]="'a11y.moduleConfig.loadingColumns' | translate"
            [theme]="{ width: '100%', height: '96px', 'border-radius': '4px', margin: '0' }" />
    } @else if (hasNoColumns()) {
        <div fxLayout="column" fxLayoutAlign="center center" fxLayoutGap="12px" class="p-a-md">
            <span class="mat-body-2 cipo-text--black-500">{{ "modules.fieldConfig.table.noColumns" | translate }}</span>
            <button
                mat-flat-button
                color="primary"
                data-testid="module-config-table-columns-action-add-first"
                [matMenuTriggerFor]="addColumnMenu"
                [disabled]="isBusy()">
                <mat-icon aria-hidden="true">add</mat-icon>
                {{ "modules.screenDesigner.addField" | translate }}
                <mat-icon iconPositionEnd aria-hidden="true">arrow_drop_down</mat-icon>
            </button>
        </div>
    } @else {
        @if (editing()) {
            <div class="table-columns__editor cipo--gap--md">
                <div class="table-columns__list">
                    <div class="table-columns__row table-columns__row--head mat-caption" aria-hidden="true">
                        <div class="table-columns__cells">
                            <span class="table-columns__label">
                                {{ "modules.fieldConfig.table.column" | translate }}
                            </span>
                            <span class="table-columns__type">{{ "modules.fieldConfig.table.type" | translate }}</span>
                            <span class="table-columns__width">
                                {{ "modules.fieldConfig.table.width" | translate }}
                            </span>
                        </div>
                    </div>
                    @if (columns().length) {
                        <div cdkDropList role="list" (cdkDropListDropped)="drop($event)">
                            @for (
                                column of columns();
                                track column.id;
                                let index = $index, first = $first, last = $last
                            ) {
                                @let badges = badgesOf(column);
                                <div
                                    cdkDrag
                                    cdkDragLockAxis="y"
                                    role="listitem"
                                    class="table-columns__row"
                                    data-testid="module-config-table-columns-column"
                                    [class.table-columns__row--changed]="isChanged(column, index)">
                                    <div class="table-columns__cells">
                                        <mat-icon
                                            cdkDragHandle
                                            class="table-columns__handle cipo--cursor--move cipo-text--black-500"
                                            aria-hidden="true">
                                            drag_indicator
                                        </mat-icon>
                                        <div class="table-columns__label">
                                            <div class="cipo--text--ellipsed" [title]="column.label">
                                                {{ column.label }}
                                            </div>
                                            <div
                                                class="mat-caption cipo-text--black-500 cipo--text--ellipsed"
                                                [title]="column.name">
                                                {{ column.name }}
                                            </div>
                                        </div>
                                        <div class="table-columns__type">
                                            <mat-icon class="cipo-text--black-500" aria-hidden="true">
                                                {{ iconOf(column) }}
                                            </mat-icon>
                                            <span class="cipo--text--ellipsed">
                                                {{ badges.typeLabel | translate }}
                                                @if (badges.displayLabel) {
                                                    · {{ badges.displayLabel | translate }}
                                                }
                                            </span>
                                            @if (isNumber(column) && column.aggregateId) {
                                                <span
                                                    class="mat-caption cipo-bg--accent-50 cipo-text--accent-900 p-x-sm cipo--border-radius--xs">
                                                    {{ columnAggregate(column) }}
                                                </span>
                                            }
                                        </div>
                                        <div class="table-columns__width">
                                            <button
                                                mat-icon-button
                                                [disabled]="saving() || !canResize() || (column.width || 1) <= 1"
                                                [title]="'modules.fieldConfig.table.narrower' | translate"
                                                (click)="resize(column, -1)">
                                                <mat-icon aria-hidden="true">chevron_left</mat-icon>
                                            </button>
                                            <span class="table-columns__units mat-body-2">{{ column.width || 1 }}</span>
                                            <button
                                                mat-icon-button
                                                [disabled]="saving() || !canResize() || (column.width || 1) >= maxWidth"
                                                [title]="'modules.fieldConfig.table.wider' | translate"
                                                (click)="resize(column, 1)">
                                                <mat-icon aria-hidden="true">chevron_right</mat-icon>
                                            </button>
                                            <div class="table-columns__bar" aria-hidden="true">
                                                <div
                                                    class="table-columns__bar-fill"
                                                    [style.width.%]="widthOf(column)"></div>
                                            </div>
                                            <span class="table-columns__percent mat-caption cipo-text--black-500">
                                                {{ percentOf(column) }}%
                                            </span>
                                        </div>
                                        <div class="table-columns__actions">
                                            <button
                                                mat-icon-button
                                                data-testid="module-config-table-columns-column-up"
                                                [disabled]="saving() || first"
                                                [title]="'modules.fieldConfig.table.moveUp' | translate"
                                                (click)="move(index, -1)">
                                                <mat-icon aria-hidden="true">arrow_upward</mat-icon>
                                            </button>
                                            <button
                                                mat-icon-button
                                                data-testid="module-config-table-columns-column-down"
                                                [disabled]="saving() || last"
                                                [title]="'modules.fieldConfig.table.moveDown' | translate"
                                                (click)="move(index, 1)">
                                                <mat-icon aria-hidden="true">arrow_downward</mat-icon>
                                            </button>
                                            <button
                                                mat-icon-button
                                                data-testid="module-config-table-columns-column-remove"
                                                [disabled]="saving()"
                                                [title]="'modules.fieldConfig.table.removeColumn' | translate"
                                                (click)="removeColumn(column)">
                                                <mat-icon aria-hidden="true">remove_circle_outline</mat-icon>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            }
                        </div>
                    } @else {
                        <p class="table-columns__empty mat-body-2 cipo-text--black-500">
                            {{ "modules.fieldConfig.table.empty" | translate }}
                        </p>
                    }
                </div>

                <h5 class="m-a-0">{{ "modules.fieldConfig.table.availableColumns" | translate }}</h5>
                <div class="table-columns__list">
                    @if (available().length) {
                        <div role="list">
                            @for (column of available(); track column.id) {
                                @let badges = badgesOf(column);
                                <div
                                    role="listitem"
                                    class="table-columns__row"
                                    data-testid="module-config-table-columns-available"
                                    [class.table-columns__row--changed]="column.unsaved">
                                    <div class="table-columns__cells">
                                        <div class="table-columns__label">
                                            <div class="cipo--text--ellipsed" [title]="column.label">
                                                {{ column.label }}
                                            </div>
                                            <div
                                                class="mat-caption cipo-text--black-500 cipo--text--ellipsed"
                                                [title]="column.name">
                                                {{ column.name }}
                                            </div>
                                        </div>
                                        <div class="table-columns__type">
                                            <mat-icon class="cipo-text--black-500" aria-hidden="true">
                                                {{ iconOf(column) }}
                                            </mat-icon>
                                            <span class="cipo--text--ellipsed">
                                                {{ badges.typeLabel | translate }}
                                                @if (badges.displayLabel) {
                                                    · {{ badges.displayLabel | translate }}
                                                }
                                            </span>
                                        </div>
                                        <div class="table-columns__actions">
                                            <button
                                                mat-icon-button
                                                color="primary"
                                                data-testid="module-config-table-columns-available-add"
                                                [disabled]="saving()"
                                                [title]="'modules.fieldConfig.table.addColumn' | translate"
                                                (click)="addColumn(column)">
                                                <mat-icon aria-hidden="true">add_circle_outline</mat-icon>
                                            </button>
                                            @if (!tableInUse() && !column.unsaved) {
                                                <button
                                                    mat-icon-button
                                                    data-testid="module-config-table-columns-available-delete"
                                                    [disabled]="saving()"
                                                    [title]="'actions.delete' | translate"
                                                    (click)="deleteColumn(column)">
                                                    <mat-icon aria-hidden="true">delete</mat-icon>
                                                </button>
                                            }
                                        </div>
                                    </div>
                                </div>
                            }
                        </div>
                    } @else {
                        <p class="table-columns__empty mat-body-2 cipo-text--black-500">
                            {{ "modules.fieldConfig.table.empty" | translate }}
                        </p>
                    }
                </div>
            </div>
        }

        <div fxLayout="column" fxLayoutGap="8px">
            <h5 class="m-a-0">{{ "modules.fieldConfig.table.preview" | translate }}</h5>
            @if (columns().length) {
                <table class="table-preview cipo--width--full" data-testid="module-config-table-columns-preview">
                    <thead>
                        <tr>
                            @for (column of columns(); track column.id) {
                                <th class="p-a-xs" [style.width.%]="widthOf(column)">
                                    <div fxLayout="row" fxLayoutAlign="center center" fxLayoutGap="4px">
                                        <span class="cipo--text--ellipsed" [title]="column.label">
                                            {{ column.label }}
                                        </span>
                                        @if (isTable() && !editing()) {
                                            <button
                                                mat-icon-button
                                                data-testid="module-config-table-columns-column-edit"
                                                [disabled]="isBusy()"
                                                [title]="'actions.edit' | translate"
                                                (click)="edit(column)">
                                                <mat-icon aria-hidden="true">edit</mat-icon>
                                            </button>
                                        }
                                    </div>
                                </th>
                            }
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            @for (column of columns(); track column.id) {
                                <td class="p-a-xs" [class.cipo--text--end]="isNumber(column)">
                                    {{
                                        isNumber(column) ? "1000" : ("modules.fieldConfig.table.sampleText" | translate)
                                    }}
                                </td>
                            }
                        </tr>
                        @if (hasAggregates()) {
                            <tr class="cipo-bg--accent-50">
                                @for (column of columns(); track column.id) {
                                    <td class="p-a-xs cipo--text--end">
                                        {{ isNumber(column) && column.aggregateId ? columnAggregate(column) : "" }}
                                    </td>
                                }
                            </tr>
                        }
                    </tbody>
                </table>
            } @else {
                <span class="mat-body-2 cipo-text--black-500">
                    {{ "modules.fieldConfig.table.noColumns" | translate }}
                </span>
            }
        </div>
    }
</div>

<mat-menu #addColumnMenu="matMenu">
    @for (fieldType of addableTypes(); track fieldType.typeId) {
        @if (fieldType.displayTypes.length > 1) {
            <button
                mat-menu-item
                data-testid="module-config-table-columns-add-type"
                [matMenuTriggerFor]="columnDisplayMenu"
                [matMenuTriggerData]="{ fieldType: fieldType }">
                <mat-icon aria-hidden="true">{{ fieldType.icon }}</mat-icon>
                {{ fieldType.label | translate }}
            </button>
        } @else {
            <button
                mat-menu-item
                data-testid="module-config-table-columns-add-type"
                (click)="add(fieldType, fieldType.displayTypes[0])">
                <mat-icon aria-hidden="true">{{ fieldType.icon }}</mat-icon>
                {{ fieldType.label | translate }}
            </button>
        }
    }
</mat-menu>

<mat-menu #columnDisplayMenu="matMenu">
    <ng-template matMenuContent let-fieldType="fieldType">
        @for (displayType of fieldType.displayTypes; track displayType.displayTypeId) {
            <button
                mat-menu-item
                data-testid="module-config-table-columns-add-display"
                (click)="add(fieldType, displayType)">
                <mat-icon aria-hidden="true">{{ displayType.icon }}</mat-icon>
                {{ displayType.label | translate }}
            </button>
        }
    </ng-template>
</mat-menu>
